import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useEmailSites } from '../hooks/useEmailSites';
import {
  useEmailDelivery,
  type DeliveryRecipient,
  type DeliverySummary,
  type RetryJob,
  type SuppressionRow,
} from '../hooks/useEmailDelivery';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'failed', label: 'Failed' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'sent', label: 'Sent' },
  { value: 'complained', label: 'Complained' },
];

export default function EmailDeliverabilityPage() {
  const { sites, defaultSiteId } = useEmailSites();
  const [siteId, setSiteId] = useState(defaultSiteId || '');
  const {
    fetchSummary,
    fetchRecipients,
    fetchRecipientDetail,
    fetchRetries,
    fetchSuppressions,
    runPreflight: runPreflightApi,
    queueRetries,
    cancelRetry,
    reviewSuppression,
  } = useEmailDelivery(siteId);

  const [summary, setSummary] = useState<DeliverySummary | null>(null);
  const [recipients, setRecipients] = useState<DeliveryRecipient[]>([]);
  const [retries, setRetries] = useState<RetryJob[]>([]);
  const [suppressions, setSuppressions] = useState<SuppressionRow[]>([]);
  const [busy, setBusy] = useState(false);

  const [statusFilter, setStatusFilter] = useState('all');
  const [emailSearch, setEmailSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [preflightText, setPreflightText] = useState('');
  const [preflightRows, setPreflightRows] = useState<
    Array<{ email: string; sendable: boolean; reason?: string }>
  >([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<{
    recipient: DeliveryRecipient | null;
    events: Array<Record<string, unknown>>;
    retryJobs: Array<Record<string, unknown>>;
  } | null>(null);

  useEffect(() => {
    if (defaultSiteId && !siteId) {
      setSiteId(defaultSiteId);
    }
  }, [defaultSiteId, siteId]);

  const refreshAll = useCallback(async () => {
    if (!siteId) {
      return;
    }
    setBusy(true);
    try {
      const [sum, rec, ret, sup] = await Promise.all([
        fetchSummary(),
        fetchRecipients({
          deliveryStatus: statusFilter === 'all' ? undefined : statusFilter,
          email: emailSearch || undefined,
        }),
        fetchRetries(),
        fetchSuppressions(emailSearch || undefined),
      ]);
      setSummary(sum);
      setRecipients(rec.rows);
      setRetries(ret.jobs);
      setSuppressions(sup.rows);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not refresh');
    } finally {
      setBusy(false);
    }
  }, [siteId, statusFilter, emailSearch, fetchSummary, fetchRecipients, fetchRetries, fetchSuppressions]);

  useEffect(() => {
    void refreshAll();
  }, [siteId]);

  const openRecipient = async (id: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const data = await fetchRecipientDetail(id);
      setDetailData(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not load details');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const runPreflight = async () => {
    const emails = preflightText
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (emails.length === 0) {
      toast.error('Paste at least one email address');
      return;
    }
    try {
      const data = await runPreflightApi(emails);
      setPreflightRows(data.rows || []);
      toast.success(`${data.sendable} of ${data.total} can be emailed`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Preflight failed');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const queueManualRetry = async (dryRun: boolean) => {
    const ids = [...selectedIds];
    if (ids.length === 0) {
      toast.error('Select at least one recipient');
      return;
    }
    try {
      const data = await queueRetries(ids, dryRun);
      if (dryRun) {
        const blocked = (data.items || []).filter((i: { eligible: boolean }) => !i.eligible);
        toast.message(
          `Preview: ${data.eligibleCount} eligible. ${blocked.length} excluded.`
        );
        return;
      }
      toast.success(`Queued ${data.created} retry job(s)`);
      void refreshAll();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retry failed');
    }
  };

  const onCancelRetry = async (jobId: string) => {
    try {
      await cancelRetry(jobId);
      toast.success('Retry cancelled');
      void refreshAll();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Cancel failed');
    }
  };

  const onDeactivateSuppression = async (row: SuppressionRow) => {
    try {
      await reviewSuppression(row.id, 'deactivate', 'Cleared from deliverability UI');
      toast.success('Suppression removed');
      void refreshAll();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update suppression');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">Email deliverability</h2>
          <p className="text-sm text-muted-foreground">
            Track delivery, block bad addresses, and retry failed sends.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="w-full sm:w-56">
              <SelectValue placeholder="Website" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void refreshAll()}>
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {summary?.health?.sendPaused ? (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="pt-4 text-sm">
            Sending is paused on the server. Campaigns and retries will wait until sending is
            turned back on.
          </CardContent>
        </Card>
      ) : null}

      <Tabs defaultValue="overview">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="preflight">Pre-send check</TabsTrigger>
          <TabsTrigger value="recipients">Recipients</TabsTrigger>
          <TabsTrigger value="retries">Retry queue</TabsTrigger>
          <TabsTrigger value="suppressions">Suppressions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Delivery summary</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {summary &&
                Object.entries(summary.byStatus).map(([status, count]) => (
                  <Badge key={status} variant="secondary">
                    {status}: {count}
                  </Badge>
                ))}
              {summary?.total === 0 ? (
                <p className="text-sm text-muted-foreground">No tracked recipients yet.</p>
              ) : null}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Worker health</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-1">
              <p>Pending retries: {summary?.health?.pendingRetryJobs ?? '—'}</p>
              <p>In progress: {summary?.health?.processingRetryJobs ?? '—'}</p>
              <p className="text-xs pt-2">
                Ensure PM2 runs <strong>tools-email-retry-worker</strong> on the server.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preflight" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pre-send check</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                placeholder="Paste emails (one per line or comma-separated)"
                value={preflightText}
                onChange={(e) => setPreflightText(e.target.value)}
                rows={6}
              />
              <Button onClick={() => void runPreflight()}>Check list</Button>
              {preflightRows.length > 0 ? (
                <ul className="max-h-64 overflow-y-auto divide-y text-sm">
                  {preflightRows.map((row) => (
                    <li key={row.email} className="flex justify-between gap-2 py-2">
                      <span className="truncate">{row.email}</span>
                      {row.sendable ? (
                        <Badge>OK</Badge>
                      ) : (
                        <span className="text-destructive text-xs">{row.reason}</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recipients" className="mt-4 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Input
              placeholder="Search email"
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.target.value)}
              className="sm:max-w-xs"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => void refreshAll()}>
              Apply filters
            </Button>
            <Button variant="outline" size="sm" onClick={() => void queueManualRetry(true)}>
              Preview retry
            </Button>
            <Button size="sm" onClick={() => void queueManualRetry(false)}>
              Retry selected
            </Button>
          </div>
          <Card>
            <CardContent className="pt-4">
              {recipients.length === 0 ? (
                <p className="text-sm text-muted-foreground">No recipients match your filters.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {recipients.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center gap-2 py-3 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        aria-label={`Select ${r.recipient_email}`}
                      />
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left font-medium hover:underline"
                        onClick={() => void openRecipient(r.id)}
                      >
                        {r.recipient_email}
                      </button>
                      <Badge variant="outline">{r.delivery_status || 'unknown'}</Badge>
                      <span className="text-muted-foreground truncate max-w-full sm:max-w-xs">
                        {r.subject}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="retries" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              {retries.length === 0 ? (
                <p className="text-sm text-muted-foreground">No retry jobs yet.</p>
              ) : (
                <ul className="divide-y divide-border text-sm">
                  {retries.map((job) => (
                    <li
                      key={job.id}
                      className="flex flex-wrap items-center gap-2 py-3"
                    >
                      <Badge>{job.status}</Badge>
                      <span>Attempt {job.attempt_number}</span>
                      <span className="text-muted-foreground">
                        Due {new Date(job.due_at).toLocaleString()}
                      </span>
                      {job.last_error ? (
                        <span className="text-destructive">{job.last_error}</span>
                      ) : null}
                      {job.status === 'pending' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void onCancelRetry(job.id)}
                        >
                          Cancel
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suppressions" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              {suppressions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No active suppressions.</p>
              ) : (
                <ul className="divide-y divide-border text-sm">
                  {suppressions.map((row) => (
                    <li key={row.id} className="flex flex-wrap items-center gap-2 py-3">
                      <span className="font-medium">{row.email_normalized}</span>
                      <Badge variant="outline">{row.reason}</Badge>
                      <span className="text-muted-foreground">{row.scope}</span>
                      {['admin_block', 'unsubscribe', 'invalid_address'].includes(row.reason) ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void onDeactivateSuppression(row)}
                        >
                          Clear block
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          Bounces and complaints need admin review
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Recipient timeline</DialogTitle>
            <DialogDescription>
              {detailData?.recipient?.recipient_email || 'Loading…'}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : detailData?.recipient ? (
            <div className="space-y-4 text-sm">
              <p>
                Status:{' '}
                <Badge variant="outline">
                  {detailData.recipient.delivery_status || 'unknown'}
                </Badge>
              </p>
              <div>
                <p className="font-medium mb-2">Delivery events</p>
                {(detailData.events || []).length === 0 ? (
                  <p className="text-muted-foreground">No events recorded yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {detailData.events.map((ev) => (
                      <li key={String(ev.id)}>
                        {String(ev.event_type)} —{' '}
                        {new Date(String(ev.event_at)).toLocaleString()}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="font-medium mb-2">Retry history</p>
                {(detailData.retryJobs || []).length === 0 ? (
                  <p className="text-muted-foreground">No retries yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {detailData.retryJobs.map((job) => (
                      <li key={String(job.id)}>
                        Attempt {String(job.attempt_number)} — {String(job.status)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
